/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { VApplication } from "../../application";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { getAgent } from "./agent";
import {
  TSupportedCountries,
  ICreatePolicyProps,
  IKYCSubmitProps,
} from "./components";

const is = require("is_js");

const SERVICE_NAME = "FHPPolicyPageService";
const log: any = LoggerModule.getInstance();

const lookup = require("country-code-lookup");

export class FHPPolicyPageService {
  private static instance: FHPPolicyPageService;
  private vapp: VApplication;
  private kycFormData: any;
  private policyData: any;
  private termsData: any;
  private intendedPurpose: any;
  private policyOptions: any;
  private country: string;
  private policyHolder: any;

  private constructor(props: any = {}) {
    this.vapp = VApplication.getInstance();
    this.kycFormData = this.kycFormData;
    this.policyData = this.policyData;
    this.termsData = this.termsData;
    this.intendedPurpose = this.intendedPurpose;
    this.policyOptions = this.policyOptions;
    this.country = this.country;
    this.policyHolder = this?.policyHolder;
  }

  public static getInstance(props: any = {}): FHPPolicyPageService {
    if (!FHPPolicyPageService.instance) {
      FHPPolicyPageService.instance = new FHPPolicyPageService(props);
    }
    return FHPPolicyPageService.instance;
  }

  private setKycFormData(data: any) {
    this.kycFormData = data;
  }
  private setPolicyData(data: any) {
    this.policyData = data;
  }

  private setPolicyOptions(data: any) {
    this.policyOptions = data;
  }

  private setTermsData(data: any) {
    this.termsData = data;
  }
  private setIntendedPurpose(data: any) {
    this.intendedPurpose = data;
  }

  private resetPolicyData() {
    (this.kycFormData = {}),
      (this.policyData = null),
      (this.policyOptions = null),
      (this.termsData = {}),
      (this.intendedPurpose = {}),
      (this.country = ""),
      (this.policyHolder = {});
  }

  public getStaticPageContent() {
    let content: any = {};
    let compProps: any = null;
    const agent = getAgent();
    compProps = agent.getStaticPageContentSync({});
    if (compProps) {
      content = { ...content, ...compProps };
    }
    return content;
  }

  public async getHeaderNavItems() {
    let content: any = {};
    let compProps: any = null;
    const agent = getAgent();
    compProps = await agent.getHeaderNavContent({});
    if (compProps) {
      content = { ...content, ...compProps };
    }
    return content;
  }

  public async getKYCData() {
    let content: any = {};
    let compProps: any = null;
    const agent = getAgent();
    compProps = await agent.getKYCFormDataContent({});
    this.policyHolder = compProps?.policyHolder;
    if (compProps) {
      content = { ...content, ...compProps };
    }
    return content;
  }

  public coverageToPremium({
    country,
    coverage,
  }: {
    country: TSupportedCountries;
    coverage: any;
  }) {
    const agent = getAgent();
    const pack = agent.coverageToPremiumSync({
      country,
      coverage: parseFloat(coverage),
    });
    log.blue(SERVICE_NAME, "coverageToPremium", pack);
    return pack;
  }

  public premiumToCoverage({
    country,
    premium,
  }: {
    country: TSupportedCountries;
    premium: number;
  }) {
    log.blue(SERVICE_NAME, "premiumToCoverage::", country, premium);
    const agent = getAgent();
    const pack = agent.premiumToCoverageSync({ country, premium });
    log.blue(SERVICE_NAME, "premiumToCoverage", pack);
    return pack;
  }

  public setKYCFormDate(formData: any) {
    this.setKycFormData(formData);
  }

  public setPolicyDetail(policyData: any) {
    this.setPolicyData(policyData);
  }

  public setTermsOfUseData(termsData: any) {
    this.setTermsData(termsData);
  }

  public SubmitIntendedPurpose(data: any) {
    this.setIntendedPurpose(data);
  }

  public onPolicyOptionChange(data: any) {
    this.setPolicyOptions(data);
  }

  public setCountry(data: any) {
    this.country = data;
  }

  public getPolicyPreviewData() {
    return {
      policyHolder:
        this.kycFormData?.firstName && this.kycFormData?.telephone
          ? {
              firstName: this.kycFormData?.firstName,
              lastName: this.kycFormData?.lastName,
              streetAddress: this.kycFormData?.streetAddress,
              city: this.kycFormData?.city,
              country: this.kycFormData?.country
                ? lookup.byIso(this.kycFormData?.country)?.country
                : "-",
              email: this.kycFormData?.email,
              telephone: this.kycFormData?.telephone,
            }
          : this.policyHolder,
      beneficiary: {
        firstName: this.kycFormData?.beneficiaryFirstName,
        lastName: this.kycFormData?.beneficiaryLastName,
        streetAddress: this.kycFormData?.beneficiaryStreetAddress,
        city: this.kycFormData?.beneficiaryCity,
        country: this.kycFormData?.beneficiaryCountry
          ? lookup.byIso(this.kycFormData?.beneficiaryCountry)?.country
          : "-",
        telephone: this.kycFormData?.beneficiaryTelephone,
        email: this.kycFormData?.beneficiaryEmail,
      },
      policyData: this.policyData,
      termsData: this.termsData,
    };
  }

  public getKYCFormData() {
    return this.kycFormData;
  }
  public getIntendedPurpose() {
    return this.intendedPurpose;
  }
  public getDefaultPolicy() {
    return this.policyOptions;
  }
  public resetFormData() {
    this.resetPolicyData();
  }

  public async getOfferData(args: any) {
    const agent = getAgent();
    return agent.getOfferDataContent(args);
  }

  public async submitKYC(args: IKYCSubmitProps) {
    log.info(`submit KYC:`, args);
    const agent = getAgent();
    return agent.getKYCSubmitProps(args);
  }
  public async createPolicy(args: ICreatePolicyProps) {
    log.info(`create policy:`, args);
    let _args = {
      beneficiaryFirstName: this.kycFormData?.beneficiaryFirstName,
      beneficiaryLastName: this.kycFormData?.beneficiaryLastName,
      beneficiaryStreetAddress: this.kycFormData?.beneficiaryStreetAddress,
      beneficiaryCity: this.kycFormData?.beneficiaryCity,
      beneficiaryCountry: this.kycFormData?.beneficiaryCountry,
      beneficiaryTelephone: this.kycFormData?.beneficiaryTelephone,
      beneficiaryEmail: this.kycFormData?.beneficiaryEmail,
      policyOption: this.policyData,
      policyCoverage: this.policyOptions,
      intendedPurpose: this.intendedPurpose,
      termsData: this.termsData,
      country: this.country,
    };
    const agent = getAgent();
    return agent.getCreatePolicyProps(_args);
  }
}
